import { ScopelyIdentity } from "@scopely/web-sdk-scopely-account";
import { ScopelyId } from "@scopely/web-sdk-scopelyid";
import { useGoogleAnalyticsContext } from "contexts";
import { LoginType } from "domain/Login/LoginGoogleAnalyticsHelper";
import { Dispatch, SetStateAction } from "react";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { IDebugLogger } from "../../../utils/debugLogger";
import {
	removeQueryParamsFromCurrentUrl,
	removeScopelyStateSession,
	setScopelyStateToSession
} from "../helpers";

export const LOGOUT_ACTION = "logout";
export const LOGOUT_NOT_CONNECTED_ACTION = "logout_not_connected";

export const useInitiateLogout = (scopelyIdentity: ScopelyIdentity | null) => {
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();

	return async (logoutActionType = LOGOUT_ACTION, origPath: string) => {
		if (!scopelyIdentity) {
			console.error("Scopely Identity is not initialized");
			return;
		}

		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "login_option_logout_start",
			event_type: "logout",
			action: "login_option_logout_start",
			login_type: LoginType.Scopely
		});
		setScopelyStateToSession(
			JSON.stringify({ action: logoutActionType, path: origPath })
		);
		await scopelyIdentity.logout(logoutActionType);
	};
	// User is redirected to identity provider logout...
};

export const useLogoutRedirect = ({
	scopelyIdentity,
	setSIDAccessToken,
	setSIDIdentityToken,
	setScopelyIdentityToken
}: {
	scopelyIdentity: ScopelyIdentity | null;
	setSIDAccessToken: Dispatch<SetStateAction<string | null>>;
	setSIDIdentityToken: Dispatch<SetStateAction<string | null>>;
	setScopelyIdentityToken: Dispatch<SetStateAction<string | null>>;
}) => {
	const { showTryReloadDialog } = useDialogModalRedux();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();

	return async (onSuccess?: () => void) => {
		if (!scopelyIdentity) {
			console.error("Scopely Identity is not initialized");
			return null;
		}

		const currentLocation = window.location.href;
		removeQueryParamsFromCurrentUrl("state");

		// Complete logout
		const result = await scopelyIdentity.logoutResult(currentLocation);
		if (result.isSuccessful()) {
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "login_option_logout_success",
				event_type: "logout",
				action: "login_option_logout_success",
				login_type: LoginType.Scopely
			});
			// Clear local session, redirect to home, etc.
			removeScopelyStateSession();
			setSIDAccessToken(null);
			setSIDIdentityToken(null);
			setScopelyIdentityToken(null);
			localStorage.removeItem("store.solitairetripeaks.com:authToken");
			localStorage.removeItem("store.solitairetripeaks.com:profileImage");
			localStorage.removeItem("store.solitairetripeaks.com:userName");
			localStorage.removeItem("store.solitairetripeaks.com:userTracker");
			localStorage.setItem("store.solitairetripeaks.com:isLoggingOut", "false");
			onSuccess && onSuccess();
		} else {
			console.error("Logout failed:", result.getError());
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "login_option_logout_failure",
				event_type: "logout",
				action: "login_option_logout_failure",
				login_type: LoginType.Scopely,
				error_type: result.getError() ?? ""
			});
			showTryReloadDialog();
		}
		return result;
	};
};

export const getStartLogout = ({
	scopelyId,
	debug
}: {
	scopelyId: ScopelyId;
	debug: IDebugLogger;
}) => {
	return async (logoutActionType = LOGOUT_ACTION, origPath: string) => {
		debug.logFunction({
			iconText: "/ScopelyIdentityProvider/api/logout",
			message: ["startLogout", logoutActionType]
		});
		setScopelyStateToSession(
			JSON.stringify({ action: logoutActionType, path: origPath })
		);
		await scopelyId.logout(logoutActionType);
	};
};

export const getTryConcludeLogout = ({
	scopelyId,
	setSIDAccessToken,
	setSIDIdentityToken,
	debug
}: {
	scopelyId: ScopelyId;
	setSIDAccessToken: Dispatch<SetStateAction<string | null>>;
	setSIDIdentityToken: Dispatch<SetStateAction<string | null>>;
	debug: IDebugLogger;
}) => {
	return async (onSuccess?: () => void) => {
		debug.logFunction({
			iconText: "/ScopelyIdentityProvider/api/logout",
			message: ["concludeLogout"]
		});

		const logoutResult = await scopelyId.logoutResult(window.location.href);
		if (logoutResult.isSuccessful()) {
			removeScopelyStateSession();
			setSIDAccessToken(null);
			setSIDIdentityToken(null);
			onSuccess && onSuccess();
		} else {
			debug.log({
				iconText: "Scopely Logout Fail",
				message: [logoutResult.getError()]
			});
		}
		return logoutResult;
	};
};
